import { formatFileSize } from '@/app/utils';
import type { FileItem, InferV1TreeNode } from '../types';

export { getModelVersionFilterOptions } from './model-version-filter-options';

/**
 * 将 API 返回的节点数据转换为 FileItem 格式
 */
export function createFileItem(node: InferV1TreeNode): FileItem {
  const { creation_task_info, parent_id, created_at, size, ...rest } = node;
  return {
    ...rest,
    size: formatFileSize(Number.parseInt(size || '0', 10)) || '0B',
    parentId: parent_id,
    createdAt: created_at,
    creationId: creation_task_info?.creation_id,
    taskId: creation_task_info?.task_id,
    coverUrl: creation_task_info?.cover_uri,
    contentUrl: creation_task_info?.uri,
    downloadUrl: creation_task_info?.download_uri,
    nomark_uri: creation_task_info?.nomark_uri,
    creationType: creation_task_info?.type,
    audioText:
      creation_task_info?.type === 'audio'
        ? creation_task_info?.prompts?.filter((p) => p.type === 'text')[0]?.content || ''
        : '',
    hasCopyright: node.creation_task_info?.has_copyright,
    hasSubFolder: node.folder_info?.has_sub_folder,
  };
}

/**
 * 文件状态更新工具函数
 * 用于在操作完成后直接更新本地文件列表状态，避免重新请求
 */
export const fileStateUpdaters = {
  /**
   * 从文件列表中删除指定的文件
   */
  removeFiles: (files: FileItem[], removedItemIds: string[]): FileItem[] => {
    const removedIds = new Set(removedItemIds);
    return files.filter((file) => !removedIds.has(file.id));
  },

  /**
   * 更新文件的收藏状态
   */
  updateFavoriteStatus: (files: FileItem[], updatedItems: FileItem[], isFavorite: boolean): FileItem[] => {
    const updatedIds = new Set(updatedItems.map((item) => item.id));
    return files.map((file) => (updatedIds.has(file.id) ? { ...file, is_favorite: isFavorite } : file));
  },

  /**
   * 移动文件（从当前列表中移除，因为移动到其他文件夹了）
   */
  moveFiles: (files: FileItem[], movedItemIds: string[]): FileItem[] => {
    return fileStateUpdaters.removeFiles(files, movedItemIds);
  },

  /**
   * 更新单个文件的属性
   */
  updateFile: (files: FileItem[], fileId: string, updates: Partial<FileItem>): FileItem[] => {
    return files.map((file) => (file.id === fileId ? { ...file, ...updates } : file));
  },

  /**
   * 批量更新多个文件的属性
   */
  updateFiles: (files: FileItem[], updates: Array<{ id: string; updates: Partial<FileItem> }>): FileItem[] => {
    const updateMap = new Map(updates.map((u) => [u.id, u.updates]));
    return files.map((file) => {
      const fileUpdates = updateMap.get(file.id);
      return fileUpdates ? { ...file, ...fileUpdates } : file;
    });
  },

  /**
   * 在文件列表前面插入新项目
   */
  prependItems: (files: FileItem[], newItems: FileItem[]): FileItem[] => {
    return [...newItems, ...files];
  },
};
